import { AppDate } from '@macs-frontend/core/utils/date';
import { UUID } from '@macs-frontend/core/utils/uuid';
import { Role } from '@macs-frontend/features/company-management/manage/models/role.model';
import { Tag } from '@macs-frontend/web-macs/shared/models/tag';
import { ProjectPermission, ProjectTrackTasksBy } from '@macs-gql';
import {
  BaseEntity,
  BudgetItem,
  BudgetItemTypes,
  Company,
  Contact,
  Invoice,
  ProjectTypes,
  ServerProjectTypes,
  SPO,
  Supplier,
  UploadedFile,
} from '..';
import { BaseTimeEntry, TimeEntry } from '../../time-tracking/time/models/time-entry';
import { SortOrders } from '../enums/sort-orders.enum';
import { BaseProjectGroup } from '../models/project-group';
import { ProjectSortTypes } from '../models/project-sort-types';
import { ActionsFormEntity } from './../../actions-form-shared/models/actions-form.model';
import { ActionTeam, ProjectMaterialGroup } from './action-team';
import { BaseBrand } from './brand';
import { BriefingInfoSettings } from './briefing-info-settings';
import { ChannelGroupSelector } from './channel-group-selector.model';
import { ChannelSelector } from './channel-selector.model';
import { Client } from './client';
import { CPO } from './cpo';
import { BaseEstimate } from './estimate.model';
import { BaseExpense, BaseExpenseGroup } from './expense';
import { ProjectTaskGroup } from './project-task-group.model';
import { ProjectTask } from './project-task.model';
import { Ratesheet } from './ratesheet.model';
import { BaseUnit } from './role.model';
import { BaseTeamMember } from './team-member.model';
import { TimeAndMaterials } from './timeAndMaterials.model';
import { BaseUser, User } from './user.model';
import { Warehouse } from './warehouse';
import { Boxed } from 'ngrx-forms';

export interface ProjectRoleUnit extends BaseUnit {
  user: BaseUser;
  role: Role;
}

export interface ProjectTeamMemberUser extends BaseUser, Pick<User, 'billableRates'> {}

export interface ProjectTeamMember extends BaseTeamMember {
  user: ProjectTeamMemberUser;
}

export interface Briefing {
  uuid: UUID;
  name: string;
  text: string;
  kind: string;
  files?: Array<UploadedFile>;
}

export interface BaseProjectExpense extends Omit<BaseEntity, 'uuid'> {
  uuid: null | UUID;
  description?: string;
  expenseUnitPrice?: number;
  billableRate?: number;
  isBillable: boolean;
  discount?: number;
  qty?: number;
  amount?: number;
}

export interface BaseProjectExpenseGroup extends BaseEntity {
  description?: string;
  isBillable: boolean;
}

export interface BaseProject extends BaseEntity {
  name: string;
  number: string;
  startDate: null | AppDate;
  endDate: null | AppDate;
  description?: string;
  starred: boolean;
  status: ProjectStatuses;
  label: string;
  forceExecuted: boolean;
  budgetType: ProjectBudgetTypes;
  fixedFee: number;
  grossMarginTarget: number;
  nettoMarginTarget: number;
  logoFilename: string;
  resolvedLogo?: string;
  nonBillableBudget: string;
  totalHours: number;
  approvedTotalHours: number;
  totalFees: null | number;
  hourlyRate: number;
  billableExpensesCost?: number;
  unitExpensesCost?: number;
  remainingHours?: number;
  approvedRemainingHours?: number;
  remainingBudget?: number;
  invoicedAmount: number;
  uninvoicedAmount?: number;
  billableTimeCost?: number;
  realTimeCost?: number;
  estimatedNettoMargin?: number;
  estimatedGrossMargin?: number;
  costContacts: number;
  currentTotalActions: number;
  currentTotalBudget: number;
  currentTotalContacts: number;
  currentTotalCost: number;
  materialsTotalBillablePrice: number;
  materialsTotalUnitPrice: number;
  grossMargin: number;
  nettoMargin?: number;
  projectId: string;
  contractProjectId?: null | string;
  lastUsedProjectId: string;
  projectType?: ProjectTypes;
  type?: ServerProjectTypes;
  draft: boolean;
  externalUuid?: UUID;
  hasCustomClientForm?: boolean;
  hasCustomCompanyForm?: boolean;
  hasCustomDebriefing?: boolean;
  trackTasksBy?: ProjectTrackTasksBy;
  actualSellPrice: number;
  actualCostPrice: number;
  confirmedEstimatesAmount: number;
  cposAmount: number;
  confirmedCposAmount: number;
  hasEstimatesCheckedForBt: boolean;
  hasDraftEstimatesCheckedForBt: boolean;
  permissions?: Record<ProjectPermission, boolean>;
  showEstimateInvoicedAmount: boolean;
  tasksTotalAmount: number;
  hasActuals: boolean;
}

export interface ProjectExpenseGroup extends BaseProjectExpenseGroup {
  originalExpenseGroup?: BaseExpenseGroup;
  expenses?: Omit<ProjectExpense, 'expenseGroup'>[];
}

export interface ProjectExpense extends BaseProjectExpense {
  expenseGroup?: ProjectExpenseGroup;
  project?: BaseProject;
  originalExpense?: BaseExpense;
}

export interface BaseTimeTrack extends BaseTimeEntry {
  billableCost: number;
  realCost: number;
}

export interface TimeTrack extends BaseTimeTrack, TimeEntry {
  projectTeamMember: ProjectTeamMember;
}

export interface Project extends BaseProject {
  customActionTeams?: ActionTeam[];
  actionTeams: ActionTeam[];
  SPOs: SPO[];
  client: Client;
  contacts: Contact[];
  CPOs: CPO[];
  timeTracks?: TimeTrack[];
  confirmedEstimates?: Invoice[];
  defaultStaffingCostBudgetItem?: BudgetItem<BudgetItemTypes.DEFAULT_STAFFING_COST>;
  defaultTeamCostBudgetItem?: BudgetItem<BudgetItemTypes.DEFAULT_TEAM_COST>;
  defaultMaterialCostBudgetItem?: BudgetItem<BudgetItemTypes.DEFAULT_MATERIAL_COST>;
  warehouses: Warehouse[];
  warehouseRatesheet: Ratesheet;
  ratesheet: Ratesheet | null;
  projectGroup: BaseProjectGroup;
  expenses?: ProjectExpense[];
  expenseGroups?: ProjectExpenseGroup[];
  tasks: ProjectTask[];
  taskGroups: ProjectTaskGroup[];
  debriefing: ActionsFormEntity;
  companyForm: ActionsFormEntity;
  clientForm: ActionsFormEntity;
  briefingInfoSettings: BriefingInfoSettings;
  tags: Tag[];
  projectMaterialGroups: ProjectMaterialGroup[];
  channelGroupSelectors: ChannelGroupSelector[];
  channelSelectors: ChannelSelector[];
  supplier?: Supplier;
  company?: Company;
  roleUnits: ProjectRoleUnit[];
  teamMembers: ProjectTeamMember[];
  mainEstimate?: Pick<BaseEstimate, 'uuid'>;
  briefing?: Briefing;
  timeAndMaterials: TimeAndMaterials;
  estimatesCount?: number;
  brand: BaseBrand;
  sentInvoicesAmount: number;
  paidInvoicesAmount: number;
  hasConfirmedEstimates: boolean;
  confirmedEstimatesCost: number;
}

export enum ProjectStatuses {
  ALL = 'ALL',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  FAVORITES = 'FAVORITES',
  DRAFT = 'DRAFT',
  MINE = 'MINE',
}

export enum ProjectFilterOptions {
  ALL = 'ALL',
  ACTIVE = 'ACTIVE',
  MINE = 'MINE',
  CANCELLED = 'CANCELLED',
  COMPLETED = 'COMPLETED',
  FAVORITES = 'FAVORITES',
}

export enum ProjectLabels {
  LOST_NO_GO = 'LOST_NO_GO',
  PITCH = 'PITCH',
  PROPOSED = 'PROPOSED',
  CONFIRMED = 'CONFIRMED',
  CPO_RECEIVED = 'CPO_RECEIVED',
  EXECUTED = 'EXECUTED',
}

export interface ProjectSorter {
  type: ProjectSortTypes;
  order: SortOrders;
}

export enum ProjectBudgetTypes {
  TimeMaterials = 'TIME_MATERIALS',
  FixedFee = 'FIXED_FEE',
  NonBillable = 'NON_BILLABLE',
}

export interface SimpleProjectForm {
  uuid: null | UUID;
  name: string;
  status: null | ProjectStatuses;
  type: null | ServerProjectTypes;
  ratesheet: null | UUID;
  client: null | UUID;
  projectId?: null | string;
}

export type ProjectPermissionsMap = Record<ProjectPermission, boolean>;
export type ProjectsCollectionPermissions = Record<string, ProjectPermissionsMap>;
